import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Droplets, ExternalLink, Copy } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pas-treasury-check/admin/stablecoin`;
const usd = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const PoolFundCard = ({ token, onChanged }) => {
  const [d, setD] = useState(null);
  const [amount, setAmount] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => axios.get(`${API}/pool`, { headers: { Authorization: `Bearer ${token}` } }).then(r => setD(r.data)).catch(() => {}), [token]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (d?.last_job?.status !== 'running') return undefined;
    const t = setInterval(load, 6000); return () => clearInterval(t);
  }, [d, load]);
  if (!d) return null;
  const amt = parseFloat(amount) || 0;
  const reserve = Math.round((amt / 2) * 100) / 100;
  const fund = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/pool/fund`, { usdc_amount: amt, confirm_text: confirm }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(r.data.message); setConfirm(''); load(); onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Funding failed'); }
    setBusy(false);
  };
  const job = d.last_job;
  return (
    <div className="bg-slate-900/60 border border-cyan-500/30 rounded-lg p-4 space-y-3" data-testid="pool-fund-card">
      <p className="text-white font-semibold flex items-center gap-2"><Droplets className="w-4 h-4 text-cyan-400" /> BETH/USDC Uniswap pool on Base</p>
      {d.pool?.exists ? (
        <p className="text-sm text-slate-300" data-testid="pool-state">Live at {usd(d.pool.price_usdc_per_beth)} per BETH · {Number(d.pool.beth_in_pool).toLocaleString()} BETH + {usd(d.pool.usdc_in_pool)} USDC ·{' '}
          <a href={d.pool.explorer_url} target="_blank" rel="noreferrer" className="text-cyan-400 inline-flex items-center gap-1">view <ExternalLink className="w-3 h-3" /></a></p>
      ) : <p className="text-sm text-slate-400" data-testid="pool-state">No pool yet.</p>}
      <div className="text-sm text-slate-300 flex flex-wrap items-center gap-2">
        Treasury USDC on Base: <strong data-testid="pool-treasury-usdc">{usd(d.treasury_usdc)}</strong>
        <button onClick={() => { navigator.clipboard.writeText(d.treasury_wallet); toast.success('Wallet address copied'); }} className="text-xs text-cyan-400 inline-flex items-center gap-1" data-testid="pool-copy-wallet">
          <Copy className="w-3 h-3" /> {d.treasury_wallet.slice(0, 10)}…{d.treasury_wallet.slice(-4)}</button>
      </div>
      <p className="text-xs text-slate-500">Send USDC on the Base network only. Half becomes the cash reserve that backs newly minted BETH; the other half is paired with that BETH in the pool at $1.00.</p>
      <div className="grid sm:grid-cols-3 gap-2">
        <input type="number" min="2" step="0.01" placeholder="USDC to use" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="pool-fund-amount"
          className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm" />
        <input placeholder="Type FUND BASE POOL" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="pool-fund-confirm"
          className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm" />
        <button onClick={fund} disabled={busy || amt < 2 || amt > d.treasury_usdc || job?.status === 'running'} data-testid="pool-fund-btn"
          className="py-2 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-40 text-white rounded-lg text-sm font-semibold">Fund pool</button>
      </div>
      {amt >= 2 && <p className="text-xs text-slate-400" data-testid="pool-fund-plan">Plan: {usd(reserve)} kept as reserve{d.pending_beth ? `, ${d.pending_beth} legacy BETH carried over` : ''} · {Number(reserve - (d.pending_beth || 0)).toFixed(2)} BETH minted · pool gets that BETH + {usd(amt - reserve)} USDC</p>}
      {job && (
        <div className="text-xs space-y-1" data-testid="pool-fund-job">
          <p className={job.status === 'failed' ? 'text-red-400' : job.status === 'completed' ? 'text-emerald-400' : 'text-amber-300'}>Last funding: {job.status}{job.error ? ` · ${job.error}` : ''}</p>
          {job.steps?.map((s, i) => <p key={i} className="text-slate-400">✓ {s.msg} {s.tx && <a href={s.tx} target="_blank" rel="noreferrer" className="text-cyan-400">tx</a>}</p>)}
        </div>
      )}
    </div>
  );
};
